import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, InputField, Modal } from '../components'
import './GroupCreate.css'

interface GroupFormData {
  name: string
  description: string
  currency: string
  privacy: 'public' | 'private'
  allowInvites: boolean
}

const GroupCreate: React.FC = () => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [formData, setFormData] = useState<GroupFormData>({
    name: '',
    description: '',
    currency: 'USD',
    privacy: 'private',
    allowInvites: true
  })
  const [errors, setErrors] = useState<Partial<GroupFormData>>({})

  const currencies = [
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
    { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
    { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
    { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' }
  ]

  const validateForm = (): boolean => {
    const newErrors: Partial<GroupFormData> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Group name is required'
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Group name must be at least 3 characters'
    } else if (formData.name.trim().length > 50) {
      newErrors.name = 'Group name must be less than 50 characters'
    }

    if (formData.description.trim().length > 200) {
      newErrors.description = 'Description must be less than 200 characters'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (field: keyof GroupFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setIsLoading(true)
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500))
      
      // Store group data in localStorage (for demo purposes)
      const groups = JSON.parse(localStorage.getItem('vibecoding_groups') || '[]')
      const newGroup = {
        id: `group_${Date.now()}`,
        ...formData,
        createdAt: new Date().toISOString(),
        members: [],
        expenses: [],
        totalExpenses: 0
      }
      groups.push(newGroup)
      localStorage.setItem('vibecoding_groups', JSON.stringify(groups))
      
      setShowSuccessModal(true)
    } catch (error) {
      console.error('Error creating group:', error)
      // Handle error state
    } finally {
      setIsLoading(false)
    }
  }

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false)
    navigate('/dashboard')
  }

  const handleBackToLanding = () => {
    navigate('/')
  }

  return (
    <div className="group-create">
      <div className="group-create-container">
        {/* Header - Consistent with Landing page hero section */}
        <div className="page-header">
          <button 
            className="back-button"
            onClick={handleBackToLanding}
            aria-label="Back to landing page"
          >
            ← Back to Home
          </button>
          <h1>Create Your Expense Group</h1>
          <p>Set up a new group to start tracking shared expenses with friends, roommates, or travel companions</p>
        </div>

        {/* Form - Consistent with Landing page feature cards */}
        <form onSubmit={handleSubmit} className="group-form">
          <div className="form-section">
            <h2>📝 Basic Information</h2>
            
            <InputField
              label="Group Name"
              placeholder="e.g., Weekend Trip, Roommates, Couple Expenses"
              value={formData.name}
              onChange={(value) => handleInputChange('name', value)}
              error={errors.name}
              required
              fullWidth
            />

            <InputField
              label="Description (Optional)"
              placeholder="Brief description of what this group is for"
              value={formData.description}
              onChange={(value) => handleInputChange('description', value)}
              error={errors.description}
              fullWidth
            />

            <div className="form-row">
              <div className="form-field">
                <label className="form-label">Currency</label>
                <select
                  value={formData.currency}
                  onChange={(e) => handleInputChange('currency', e.target.value)}
                  className="currency-select"
                >
                  {currencies.map(currency => (
                    <option key={currency.code} value={currency.code}>
                      {currency.symbol} {currency.code} - {currency.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>🔒 Privacy & Settings</h2>
            
            <div className="form-field">
              <label className="form-label">Privacy Level</label>
              <div className="radio-group">
                <label className="radio-option">
                  <input
                    type="radio"
                    name="privacy"
                    value="private"
                    checked={formData.privacy === 'private'}
                    onChange={(e) => handleInputChange('privacy', e.target.value)}
                  />
                  <span className="radio-custom"></span>
                  <div className="radio-content">
                    <span className="radio-label">Private Group</span>
                    <span className="radio-description">Only invited members can see and join. Perfect for personal expenses and close groups.</span>
                  </div>
                </label>
                
                <label className="radio-option">
                  <input
                    type="radio"
                    name="privacy"
                    value="public"
                    checked={formData.privacy === 'public'}
                    onChange={(e) => handleInputChange('privacy', e.target.value)}
                  />
                  <span className="radio-custom"></span>
                  <div className="radio-content">
                    <span className="radio-label">Public Group</span>
                    <span className="radio-description">Anyone with the link can join. Great for events, clubs, or open communities.</span>
                  </div>
                </label>
              </div>
            </div>

            <div className="form-field">
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={formData.allowInvites}
                  onChange={(e) => handleInputChange('allowInvites', e.target.checked)}
                />
                <span className="checkbox-custom"></span>
                <div className="checkbox-content">
                  <span className="checkbox-label">Allow members to invite others</span>
                  <span className="checkbox-description">Members can send invitations to new people, making it easier to grow your group.</span>
                </div>
              </label>
            </div>
          </div>

          <div className="form-section">
            <h2>🚀 What Happens Next?</h2>
            <div className="next-steps">
              <div className="step-item">
                <div className="step-icon">1</div>
                <div className="step-content">
                  <h3>Group Created</h3>
                  <p>Your expense group will be set up with the settings you've chosen and you'll be added as the first member.</p>
                </div>
              </div>
              
              <div className="step-item">
                <div className="step-icon">2</div>
                <div className="step-content">
                  <h3>Invite Members</h3>
                  <p>Share the group link with friends and family to start tracking expenses together. No account required to join!</p>
                </div>
              </div>
              
              <div className="step-item">
                <div className="step-icon">3</div>
                <div className="step-content">
                  <h3>Start Tracking</h3>
                  <p>Begin adding expenses and see balances update in real-time. Everyone stays in sync automatically.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions - Consistent with Landing page CTA section */}
          <div className="form-actions">
            <Button
              type="button"
              variant="ghost"
              onClick={handleBackToLanding}
              disabled={isLoading}
            >
              Cancel
            </Button>
            
            <Button
              type="submit"
              variant="primary"
              loading={isLoading}
              disabled={isLoading}
            >
              {isLoading ? 'Creating Group...' : 'Create Group'}
            </Button>
          </div>
        </form>
      </div>

      {/* Success Modal - Consistent with Landing page styling */}
      <Modal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        title="🎉 Group Created Successfully!"
        size="small"
        showCloseButton={false}
        closeOnOverlayClick={false}
        closeOnEscape={false}
      >
        <div className="success-content">
          <div className="success-icon">✨</div>
          <h3>Your group "{formData.name}" is ready!</h3>
          <p>You can now invite members and start tracking expenses together. The group has been saved and you're all set to begin!</p>
          
          <div className="success-actions">
            <Button
              variant="primary"
              onClick={handleSuccessModalClose}
              fullWidth
            >
              Go to Dashboard
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default GroupCreate 